/**
 * Workspace persistence facade.
 * Multi-project storage lives in projects.ts; this module keeps the previous
 * public API used by the skin store and legacy single-workspace helpers.
 */

import {
  getActiveProjectId,
  hydrateProjectsFromStorage,
  persistActiveProject,
  type ProjectMeta,
} from "./projects";

export { WORKSPACE_KEY } from "./projects-constants";
export { WorkspacePersistenceError } from "./projects-constants";

export type WorkspaceAutosaver = {
  schedule: () => void;
  flush: () => Promise<void>;
  cancel: () => void;
};

/** Capture current app state into the active project. */
export async function persistCurrentWorkspace(): Promise<void> {
  const activeId = getActiveProjectId();
  if (!activeId) {
    // During first boot before a project exists, no-op.
    return;
  }
  await persistActiveProject(activeId, undefined, { activate: false });
}

export function createWorkspaceAutosaver(
  delayMilliseconds = 600,
  callbacks: {
    onSaved?: () => void;
    onError?: (error: unknown) => void;
  } = {},
): WorkspaceAutosaver {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let pending = false;
  let chain: Promise<void> = Promise.resolve();

  const flush = async (): Promise<void> => {
    if (timer) clearTimeout(timer);
    timer = undefined;
    if (!pending) return;
    pending = false;
    try {
      await persistCurrentWorkspace();
      callbacks.onSaved?.();
    } catch (error) {
      callbacks.onError?.(error);
      throw error;
    }
  };

  const enqueueFlush = (): Promise<void> => {
    chain = chain.then(flush).catch(() => undefined);
    return chain;
  };

  return {
    schedule() {
      pending = true;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        void enqueueFlush();
      }, delayMilliseconds);
    },
    flush: enqueueFlush,
    cancel() {
      if (timer) clearTimeout(timer);
      timer = undefined;
      pending = false;
    },
  };
}

/**
 * Restore multi-project workspace (migrates legacy single workspace if needed).
 */
export async function hydrateWorkspaceFromStorage(): Promise<boolean> {
  const result = await hydrateProjectsFromStorage();
  return result.restored || result.projects.length > 0;
}

export type { ProjectMeta };
